"use client";

import * as React from "react";

import { Drawer as DrawerPrimitive } from "vaul";

import { PortalWrapper } from "../prim.portal-wrapper";
import { cn } from "../util.cn";

const Drawer = ({
  shouldScaleBackground = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      {...props}
    />
  );
};
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = (
  props: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Portal>,
) => {
  const { children, ...portalProps } = props;
  return (
    <DrawerPrimitive.Portal {...portalProps}>
      <PortalWrapper>
        {children}
      </PortalWrapper>
    </DrawerPrimitive.Portal>
  );
};
const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Overlay
      className={cn("fixed inset-0 z-50 bg-overlay", className)}
      ref={ref}
      {...props}
    />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ children, className, ...props }, ref) => {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        className={cn(
        `
          fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto
          flex-col rounded-t-5 bg-panel shadow-3
        `,
        className,
        )}
        ref={ref}
        {...props}
      >
        <div className={`
          mx-auto my-3 h-2 w-[100px] rounded-full bg-gray-a8
        `}
        />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(`
        grid gap-1.5 p-4 text-center
        sm:text-left
      `, className)}
      {...props}
    />
  );
};
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
};
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Title
      className={cn(
        "text-4 font-semibold",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DrawerPrimitive.Description
      className={cn("text-2", className)}
      ref={ref}
      {...props}
    />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer as Root,
  DrawerClose as Close,
  DrawerContent as Content,
  DrawerDescription as Description,
  DrawerFooter as Footer,
  DrawerHeader as Header,
  DrawerOverlay as Overlay,
  DrawerPortal as Portal,
  DrawerTitle as Title,
  DrawerTrigger as Trigger,
};
